import { useState, type ChangeEvent, type FormEvent } from 'react';
import { Modal } from '../../components/Modal';
import { planErrorMessage } from '../../components/plan/planErrors';
import { TicketHead } from '../../components/TicketHead';
import { TokenListEditor } from '../../components/TokenListEditor';
import { getErrorMessage } from '../../lib/errors';
import {
  IMAGE_ACCEPT_ATTRIBUTE,
  assetFileName,
  uploadImageToAssets,
  validateImageFile,
} from '../../lib/storage';
import type { AssetKind, AssetRead, AssetUpdate } from '../../types/api';
import { AssetPreview } from './AssetPreview';
import { assetsApi } from './knowledgeBaseApi';
import { SectionShell } from './SectionShell';
import { useCollection } from './useCollection';
import { useSectionCapacity } from './useSectionCapacity';

const KIND_OPTIONS: readonly { value: AssetKind; label: string }[] = [
  { value: 'product_photo', label: 'Product photo' },
  { value: 'background', label: 'Background' },
  { value: 'logo', label: 'Logo' },
  { value: 'other', label: 'Other' },
];

/** Also the subject of the storage-cap message. */
const SECTION_TITLE = 'Images & logos';

function kindLabel(kind: AssetKind): string {
  return KIND_OPTIONS.find((option) => option.value === kind)?.label ?? kind;
}

interface AssetFormProps {
  initial: AssetRead;
  isSaving: boolean;
  error: string | null;
  onSubmit: (body: AssetUpdate) => void;
  onCancel: () => void;
}

function AssetForm({ initial, isSaving, error, onSubmit, onCancel }: AssetFormProps) {
  const [kind, setKind] = useState<AssetKind>(initial.kind);
  const [tags, setTags] = useState<string[]>(initial.tags);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    onSubmit({ kind, tags });
  }

  return (
    <form className="form" onSubmit={handleSubmit} noValidate>
      <AssetPreview storagePath={initial.storage_path} variant="logo" />

      <label className="field">
        <span className="field__label">Kind</span>
        <select
          className="field__input"
          value={kind}
          onChange={(event) => setKind(event.target.value as AssetKind)}
        >
          {KIND_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>

      <TokenListEditor
        label="Tags"
        hint="How you would search for this file later."
        placeholder="storefront"
        addLabel="Add tag"
        tokens={tags}
        onChange={setTags}
      />

      {error ? (
        <p className="form__error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="modal__actions">
        <button className="button" type="button" onClick={onCancel} disabled={isSaving}>
          Cancel
        </button>
        <button className="button button--primary" type="submit" disabled={isSaving}>
          {isSaving ? 'Saving' : 'Save'}
        </button>
      </div>
    </form>
  );
}

/**
 * Assets — images uploaded to the private `assets` Storage bucket through a
 * signed URL the API mints (after checking the storage cap), then recorded
 * through the API. Previews use short-lived signed URLs.
 */
export function AssetsSection({ businessId }: { businessId: string }) {
  const collection = useCollection(assetsApi, businessId);
  const { capacity, isFull, planName, fullTitle, refreshCapacity } = useSectionCapacity(
    businessId,
    'storage_bytes',
  );
  const [uploadKind, setUploadKind] = useState<AssetKind>('product_photo');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [editing, setEditing] = useState<AssetRead | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [writeError, setWriteError] = useState<string | null>(null);

  async function handleUpload(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    if (files.length === 0) return;

    const rejected = files.map(validateImageFile).find((message) => message !== null);
    if (rejected) {
      setUploadError(rejected);
      return;
    }

    setUploadError(null);
    setIsUploading(true);
    try {
      for (const file of files) {
        const storagePath = await uploadImageToAssets(businessId, file);
        await collection.create({
          storage_path: storagePath,
          kind: uploadKind,
          tags: [],
          product_id: null,
        });
      }
    } catch (caught) {
      setUploadError(
        planErrorMessage(caught, 'Upload failed. Try again.', {
          plan: planName,
          subject: SECTION_TITLE,
        }),
      );
    } finally {
      setIsUploading(false);
      // Bytes moved even when a later file of the batch was refused.
      refreshCapacity();
    }
  }

  async function handleSubmit(body: AssetUpdate) {
    if (!editing) return;
    setIsSaving(true);
    setWriteError(null);
    try {
      await collection.update(editing.id, body);
      setEditing(null);
    } catch (caught) {
      setWriteError(getErrorMessage(caught, 'That image did not save.'));
    } finally {
      setIsSaving(false);
    }
  }

  /**
   * Optimistic: the card goes now, the DELETE waits out the undo window. Same
   * commit as before — the metadata row only. The uploaded object stays in the
   * bucket, exactly as it did with the confirm dialog.
   */
  function startDelete(item: AssetRead) {
    collection.removeWithUndo({
      id: item.id,
      label: assetFileName(item.storage_path),
      failureMessage: 'That image could not be deleted.',
      onRemoved: refreshCapacity,
    });
  }

  return (
    <>
      <SectionShell
        title={SECTION_TITLE}
        lede="Product photos, backgrounds and logos we can use in your posts."
        action={
          <div className="upload-control">
            <label className="field field--inline">
              <span className="field__label">Upload as</span>
              <select
                className="field__input"
                value={uploadKind}
                disabled={isUploading}
                onChange={(event) => setUploadKind(event.target.value as AssetKind)}
              >
                {KIND_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label
              className="button button--primary"
              aria-disabled={isUploading || isFull}
              title={fullTitle}
            >
              {isUploading ? 'Uploading' : '+ Upload image or logo'}
              <input
                className="visually-hidden"
                type="file"
                multiple
                accept={IMAGE_ACCEPT_ATTRIBUTE}
                disabled={isUploading || isFull}
                onChange={(event) => void handleUpload(event)}
              />
            </label>
          </div>
        }
        capacity={capacity}
        capacityInBytes
        isLoading={collection.isLoading}
        error={collection.error ?? collection.deleteError ?? uploadError}
        isEmpty={collection.items.length === 0 && !isUploading}
        emptyMessage="No images or logos yet. Upload the photos you want on slides. PNG, JPEG, WebP or GIF, under 5 MB."
      >
        <ul className="asset-grid">
          {collection.items.map((item) => (
            <li className="asset-card" key={item.id}>
              <TicketHead label="Image" timestamp={item.created_at} />
              <AssetPreview storagePath={item.storage_path} />
              <div className="asset-card__body">
                <span className="asset-card__name">{assetFileName(item.storage_path)}</span>
                <span className="badge badge--neutral">{kindLabel(item.kind)}</span>
                {item.tags.length > 0 ? (
                  <ul className="token-list token-list--static">
                    {item.tags.map((tag) => (
                      <li className="token" key={tag}>
                        {tag}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
              <div className="record__actions">
                <button
                  className="button button--ghost"
                  type="button"
                  onClick={() => {
                    setWriteError(null);
                    setEditing(item);
                  }}
                >
                  Edit
                </button>
                <button
                  className="button button--ghost button--danger-text"
                  type="button"
                  onClick={() => startDelete(item)}
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      </SectionShell>

      {editing ? (
        <Modal title="Edit image" kind="asset" onDismiss={() => setEditing(null)}>
          <AssetForm
            initial={editing}
            isSaving={isSaving}
            error={writeError}
            onSubmit={(body) => void handleSubmit(body)}
            onCancel={() => setEditing(null)}
          />
        </Modal>
      ) : null}
    </>
  );
}
